import { Search, SlidersHorizontal, X, Grid, List } from 'lucide-react'
import type { ToolFilters, DangerLevel, ViewMode } from '@/types/plugins'

interface ToolSearchBarProps {
  filters: ToolFilters
  onFiltersChange: (filters: ToolFilters) => void
  viewMode: ViewMode
  onViewModeChange: (mode: ViewMode) => void
  pluginOptions: string[]
  resultCount: number
}

const dangerLevels: DangerLevel[] = ['SAFE', 'LOW', 'MEDIUM', 'HIGH', 'CRITICAL']

export default function ToolSearchBar({
  filters,
  onFiltersChange,
  viewMode,
  onViewModeChange,
  pluginOptions,
  resultCount,
}: ToolSearchBarProps) {
  const hasActiveFilters =
    filters.plugin !== null ||
    filters.dangerLevel !== null ||
    filters.minSatisfaction !== null

  const clearFilters = () => {
    onFiltersChange({
      ...filters,
      plugin: null,
      dangerLevel: null,
      minSatisfaction: null,
    })
  }

  return (
    <div className="bg-surface border border-surface-border rounded-xl p-3 sm:p-4">
      {/* Search Input */}
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <input
            type="text"
            placeholder="Search tools..."
            value={filters.search}
            onChange={(e) =>
              onFiltersChange({ ...filters, search: e.target.value })
            }
            className="w-full pl-10 pr-4 py-2.5 bg-background border border-surface-border rounded-lg text-text placeholder:text-text-muted focus:outline-none focus:border-accent/50 text-sm"
          />
          {filters.search && (
            <button
              onClick={() => onFiltersChange({ ...filters, search: '' })}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-surface-hover rounded"
            >
              <X className="w-3.5 h-3.5 text-text-muted" />
            </button>
          )}
        </div>

        {/* View Mode Toggle */}
        <div className="flex border border-surface-border rounded-lg overflow-hidden">
          <button
            onClick={() => onViewModeChange('grid')}
            className={`p-2.5 transition-colors ${
              viewMode === 'grid'
                ? 'bg-accent/10 text-accent'
                : 'text-text-muted hover:bg-surface-hover'
            }`}
            title="Grid view"
          >
            <Grid className="w-4 h-4" />
          </button>
          <button
            onClick={() => onViewModeChange('list')}
            className={`p-2.5 transition-colors ${
              viewMode === 'list'
                ? 'bg-accent/10 text-accent'
                : 'text-text-muted hover:bg-surface-hover'
            }`}
            title="List view"
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <SlidersHorizontal className="w-4 h-4 text-text-muted" />

        {/* Plugin Filter */}
        <select
          value={filters.plugin ?? ''}
          onChange={(e) =>
            onFiltersChange({
              ...filters,
              plugin: e.target.value || null,
            })
          }
          className="px-3 py-1.5 bg-background border border-surface-border rounded-lg text-sm text-text focus:outline-none focus:border-accent/50"
        >
          <option value="">All Plugins</option>
          {pluginOptions.map((plugin) => (
            <option key={plugin} value={plugin}>
              {plugin}
            </option>
          ))}
        </select>

        {/* Danger Level Filter */}
        <select
          value={filters.dangerLevel ?? ''}
          onChange={(e) =>
            onFiltersChange({
              ...filters,
              dangerLevel: (e.target.value as DangerLevel) || null,
            })
          }
          className="px-3 py-1.5 bg-background border border-surface-border rounded-lg text-sm text-text focus:outline-none focus:border-accent/50"
        >
          <option value="">Any Danger</option>
          {dangerLevels.map((level) => (
            <option key={level} value={level}>
              {level}
            </option>
          ))}
        </select>

        {/* Sort */}
        <select
          value={`${filters.sortBy}-${filters.sortOrder}`}
          onChange={(e) => {
            const [sortBy, sortOrder] = e.target.value.split('-') as [
              ToolFilters['sortBy'],
              ToolFilters['sortOrder'],
            ]
            onFiltersChange({ ...filters, sortBy, sortOrder })
          }}
          className="px-3 py-1.5 bg-background border border-surface-border rounded-lg text-sm text-text focus:outline-none focus:border-accent/50"
        >
          <option value="name-asc">Name A-Z</option>
          <option value="name-desc">Name Z-A</option>
          <option value="calls-desc">Most Used</option>
          <option value="calls-asc">Least Used</option>
          <option value="satisfaction-desc">Highest Rated</option>
          <option value="satisfaction-asc">Lowest Rated</option>
        </select>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="px-3 py-1.5 text-sm text-accent hover:bg-accent/10 rounded-lg transition-colors flex items-center gap-1"
          >
            <X className="w-3.5 h-3.5" />
            Clear
          </button>
        )}

        {/* Result Count */}
        <div className="ml-auto text-xs text-text-muted">
          {resultCount} {resultCount === 1 ? 'tool' : 'tools'}
        </div>
      </div>
    </div>
  )
}
